<template>
  <div class="TripDisplayTimeInfo" :class="`TripDisplayTimeInfo--${props.alignment}`">
    <p v-if="timezone" class="TripDisplayTimeInfo-timezone">
      ({{ timezone }})
    </p>

    <p class="TripDisplayTimeInfo-time">
      <slot name="time">
        {{ time }}
      </slot><span v-if="dayOffset" class="TripDisplayTimeInfo-dayOffset">{{ dayOffsetFormatted }}</span>
    </p>

    <p v-if="location" class="TripDisplayTimeInfo-location">
      {{ location }}
    </p>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import type { TripDisplayTimeInfoProps } from './TripDisplayTimeInfo.model';

const props = withDefaults(defineProps<TripDisplayTimeInfoProps>(), {
  alignment: 'start',
});

const dayOffsetFormatted = computed<string | null>(() => {
  if (props.dayOffset !== undefined && props.dayOffset !== null) {
    return props.dayOffset > 0 ? `+${props.dayOffset}` : `${props.dayOffset}`;
  }
  return null;
});
</script>

<style lang="scss">
@use '@cvo/ui/styles/design-system' as vars;
@use '@cvo/ui/styles/utils/layout';
@use '@cvo/ui/styles/utils/text';
@use '@cvo/ui/styles/utils/responsive';

@layer cvo-ui {
  .TripDisplayTimeInfo {
    display: flex;
    flex-direction: column;
    gap: vars.$dimensions-d-1;

    &--start {
      align-items: flex-start;
    }

    &--end {
      align-items: flex-end;

      .TripDisplayTimeInfo-timeContainer {
        justify-content: end;
      }
    }

    &-timezone {
      color: vars.$organisms-tripdisplay-segmentdetails-direct-default-text-secondary-color;
      @include text.text(xs);
    }

    &-time {
      position: relative;
      color: vars.$organisms-tripdisplay-segmentdetails-direct-default-text-time-color;
      @include text.heading(h3);
      font-weight: vars.$typography-weight-heading;

      .Time-suffix {
        font-weight: vars.$typography-weight-strong;
      }

      @container(max-width: #{vars.$screen-sm-max-width}) {
        color: vars.$organisms-tripdisplay-segmentdetails-direct-default-text-time-color;;
        @include text.heading(h6);
      }

      .TripDisplayTimeInfo-dayOffset {
        position: absolute;
        top: -6px;
        inset-inline-end: -8px;
        vertical-align: super;

        color: vars.$organisms-tripdisplay-segmentdetails-direct-default-text-highlight-color;
        @include text.text(xs);
        @container(max-width: #{vars.$screen-sm-max-width}) {
          position: static;
        }
      }
    }

    &-location {
      color: vars.$organisms-tripdisplay-segmentdetails-direct-default-text-secondary-color;
      @include text.text(xs);
    }
  }
}
</style>